import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import {
  CompanyListFacilitiesFilterParam,
  Facility,
  FacilityCreationPayload,
  FacilityUpdatePayload,
  Ramp,
} from '@shared-types/domain'
import { Collection, Identifier, Nullable } from '@shared-types/util'

export interface FacilitiesClient {
  list: (
    companyId: Identifier,
    params?: CompanyListFacilitiesFilterParam
  ) => Promise<Collection<Facility>>
  listAll: (companyId: Identifier) => Promise<Facility[]>
  create: (companyId: Identifier, payload: FacilityCreationPayload) => Promise<Facility>
  getRamps: (companyId: Identifier, facilityId: Identifier) => Promise<Ramp[]>
  update: (
    companyId: Identifier,
    facilityId: Identifier,
    payload: FacilityUpdatePayload
  ) => Promise<Facility>
  getFacilityAnalyticsDashboard: (
    companyId: Identifier,
    facilityId: Identifier
  ) => Promise<{ url: Nullable<string> }>
}

class DefaultFacilitiesClient implements FacilitiesClient {
  client: ApiClient

  constructor() {
    this.client = initApiClient({})
  }

  async list(
    companyId: Identifier,
    params?: CompanyListFacilitiesFilterParam
  ): Promise<Collection<Facility>> {
    return this.client
      .get(`/companies/${companyId}/facilities`, { params })
      .then(({ data }) => data)
  }
  async listAll(companyId: Identifier): Promise<Facility[]> {
    return this.client.get(`/companies/${companyId}/facilities/_all`).then(({ data }) => data)
  }
  async create(companyId: Identifier, payload: FacilityCreationPayload): Promise<Facility> {
    return this.client.post(`/companies/${companyId}/facilities`, payload).then((res) => res.data)
  }

  async getRamps(companyId: Identifier, facilityId: Identifier): Promise<Ramp[]> {
    return this.client
      .get(`/companies/${companyId}/facilities/${facilityId}/ramps`)
      .then((res) => res.data)
  }

  async update(
    companyId: Identifier,
    facilityId: Identifier,
    payload: FacilityUpdatePayload
  ): Promise<Facility> {
    return this.client
      .patch(`/companies/${companyId}/facilities/${facilityId}`, payload)
      .then((res) => res.data)
  }

  async getFacilityAnalyticsDashboard(
    companyId: Identifier,
    facilityId: Identifier
  ): Promise<{ url: Nullable<string> }> {
    return this.client
      .get(`/companies/${companyId}/facilities/${facilityId}/analytics_dashboard`)
      .then((res) => res.data)
  }
}

const FacilitiesClientContext = createContext<FacilitiesClient>(new DefaultFacilitiesClient())

export function useFacilitiesClient() {
  return useContext(FacilitiesClientContext)
}
